import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Skeleton } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type RevenueSummarySkeletonProps = {
    'data-testid': string;
    id: string;
};

/**
 * Placeholder grid that mirrors `RevenueSummaryView`'s layout while the data
 * is loading. Renders four card-shaped skeletons so the layout doesn't shift
 * when the real KPIs swap in.
 */
export const RevenueSummarySkeleton: React.FC<RevenueSummarySkeletonProps> = ({
    'data-testid': dataTestId,
    id,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.revenueSummary.title'),
        }),
        [t],
    );

    return (
        <section
            aria-labelledby={`${id}-title`}
            aria-busy
            className="flex flex-col gap-4"
            {...getNodeTestIdProp(dataTestId)}
            id={id}
        >
            <h2
                className="text-base font-semibold text-foreground"
                {...getNodeTestIdProp(`${id}-title`)}
                id={`${id}-title`}
            >
                {copy.title}
            </h2>
            <div
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
                {...getNodeTestIdProp(`${id}-grid`)}
                id={`${id}-grid`}
            >
                {Array.from({ length: 4 }).map((_, index) => {
                    const placeholderId = `${id}-placeholder-${index}`;

                    return (
                        <Card
                            className="flex flex-col gap-3 p-4"
                            data-testid={placeholderId}
                            id={placeholderId}
                            key={placeholderId}
                        >
                            <div className="flex items-center gap-2">
                                <Skeleton className="size-8 rounded-md" />
                                <Skeleton className="h-3 w-20 rounded-md" />
                            </div>
                            <Skeleton className="h-7 w-28 rounded-md" />
                        </Card>
                    );
                })}
            </div>
        </section>
    );
};
